import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { getSalesOrderId } from 'services/salesOrders';
import { getRepresentatives } from 'services/representatives';
import { getCustomers } from 'services/customers';
import { getOrderPriorities } from 'services/settings/orderPriorities';
import { getLocations } from 'services/locations';
import { getCarriers } from 'services/carriers';
import { getPaymentTerms } from 'services/paymentTerms';
import { Pagination } from 'services/search';
import { fetchTagsAPI, Tag } from 'services/tags';
import { useUrlQueryObject } from 'services/url';
import {
  clearModuleNavigation,
  ModuleNavigationType,
  removeModuleNavigation,
} from 'services/moduleNavigation';
import { useBackgroundTasks } from 'services/backgroundTasks';
import { Dates, formatDate } from 'helpers';
import { useGetIntlDateFormatString, usePageSettings } from 'helpers/hooks';
import { SalesOrderPageProps } from './types';
import { salesOrderAdvSearchFields } from './consts';
import {
  SalesOrderPageAction,
  SalesOrderTableRowActions,
} from './components/SalesOrderSearchResults/types';
import { useLocation, useNavigate } from 'react-router-dom';
import SalesOrderPageView from './SalesOrderPage.view';
import { useSalesOrderFileOperations } from '../../hooks/useSalesOrderFileOperations';
import { useSalesOrderBulkActions } from '../../hooks/useSalesOrderBulkActions';

export default function SalesOrderPageContainer(props: SalesOrderPageProps) {
  const {
    searchState: searchResult,
    refreshSearchState: fetchSearchResult,
    isLoadingSearchState: isLoadingSearchResult,
    activeItemId: activeSalesOrderId,
  } = props;

  const dispatch = useDispatch();

  const { items: customers } = useSelector(getCustomers);
  const { items: representatives } = useSelector(getRepresentatives);
  const { items: carriers } = useSelector(getCarriers);
  const { items: orderPriorities } = useSelector(getOrderPriorities);
  const { items: paymentTerms } = useSelector(getPaymentTerms);
  const { items: locations } = useSelector(getLocations);

  const { storedValue, setPageSettings } = usePageSettings();

  const [nextSalesOrdersNumber, setNextSalesOrdersNumber] =
    useState<number>(-1);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [clone, setClone] = useState<boolean>(false);
  const [activeDate, setActiveDate] = useState<Dates>(Dates.DateCreated);

  const navigate = useNavigate();
  const location = useLocation();
  const [, extendUrlQuery] = useUrlQueryObject(navigate, location);

  const { startFetching, startCsvFetching } = useBackgroundTasks();
  const dateFormatPattern = useGetIntlDateFormatString();

  useEffect(() => {
    const tagIds = _.get(
      searchResult.advancedSearch.columns,
      [salesOrderAdvSearchFields.Tags],
      null
    ) as number[];

    if (tagIds) {
      (async () => {
        try {
          const resTags = await fetchTagsAPI({}, tagIds);

          setSearchTags(resTags.data);
        } catch {
          //ignore Error
        }
      })();
    }
  }, [searchResult.advancedSearch.columns]);

  useEffect(() => {
    dispatch(clearModuleNavigation(ModuleNavigationType.Sales));
    setActiveDate(storedValue.salesOrder.dateColumn);

    return () => {
      dispatch(removeModuleNavigation());
    };
  }, []);

  const getNextSalesOrdersNumber = useCallback(async () => {
    const nextNumber = await getSalesOrderId();
    setNextSalesOrdersNumber(nextNumber || -1);
  }, []);

  const handleActiveSalesOrderClose = useCallback(
    () => extendUrlQuery({ activeId: null }),
    [extendUrlQuery]
  );

  const handleAddNewPress = useCallback(async () => {
    await getNextSalesOrdersNumber();
    setClone(false);
    extendUrlQuery({ activeId: -1 });
  }, [getNextSalesOrdersNumber, extendUrlQuery]);

  const handleSalesOrderClick = useCallback(
    async (salesOrderId: number) => {
      extendUrlQuery({ activeId: salesOrderId });
      setClone(false);
    },
    [extendUrlQuery]
  );

  const handlePaginationChange = useCallback(
    (newPagination: Pagination) => {
      fetchSearchResult({ pagination: newPagination });
    },
    [fetchSearchResult]
  );

  const handleAction = useCallback(
    (type: SalesOrderTableRowActions, value: any) => {
      if (type === SalesOrderTableRowActions.Duplicate) {
        setClone(true);
        extendUrlQuery({ activeId: value });
      } else if (type === SalesOrderTableRowActions.Delete) {
        setSelectedItems([value as number]);
      }
    },
    [extendUrlQuery, setClone, setSelectedItems]
  );

  const {
    fileImportInputRef,
    onTriggerImport,
    onFileSelectedForImport,
    onExport,
  } = useSalesOrderFileOperations({
    onExportStarted: startFetching,
    onImportInitiated: startCsvFetching,
  });

  const { executeBulkAction, isLoading: isBulkActionLoading } =
    useSalesOrderBulkActions({
      selectedIds: selectedItems,
      fetchSearchResult,
      onSuccess: () => setSelectedItems([]),
      // onError: (error, action) => { /* Handle errors, e.g., show a notification */ }
    });

  const handlePageAction = useCallback(
    async (action: SalesOrderPageAction, value?: any) => {
      switch (action) {
        case SalesOrderPageAction.DeleteMultiple:
          await executeBulkAction('delete');
          break;
        case SalesOrderPageAction.Unissue:
          await executeBulkAction('unissue');
          break;
        case SalesOrderPageAction.Issue:
          await executeBulkAction('issue');
          break;
        case SalesOrderPageAction.Export:
          onExport();
          break;
        case SalesOrderPageAction.Import:
          onTriggerImport();
          break;
        case SalesOrderPageAction.ChangeDate:
          setActiveDate(value!);
          setPageSettings('salesOrder', 'dateColumn', value);
          break;
      }
    },
    [executeBulkAction, onExport, onTriggerImport, setPageSettings]
  );

  const [searchTags, setSearchTags] = useState<Tag[]>([]);

  const intlFormatDateMemo = useMemo(
    () => formatDate(activeDate as any, null, dateFormatPattern),
    [activeDate, dateFormatPattern]
  );

  const viewProps = {
    data: {
      searchResult,
      activeSalesOrderId,
      searchTags,
      intlFormatDate: intlFormatDateMemo,
    },
    uiState: {
      isLoading: isLoadingSearchResult || isBulkActionLoading,
      isLoadingSearchResult,
      activeDate,
      nextSalesOrdersNumber,
      clone,
      fileImportInputRef,
    },
    lookups: {
      carriers,
      customers,
      locations,
      orderPriorities,
      paymentTerms,
      representatives,
    },
    selection: {
      selectedItems,
      setSelectedItems,
    },
    handlers: {
      fetchSearchResult,
      handleAction,
      handleActiveSalesOrderClose,
      handleAddNewPress,
      handlePageAction,
      handlePaginationChange,
      handleSalesOrderClick,
      setClone,
      setNextSalesOrdersNumber,
      onTriggerImport,
      onFileSelectedForImport,
      onExport,
    },
  };

  return <SalesOrderPageView {...viewProps} />;
}
